// Modal de overrides de config a nivel client.
//
// Cada client de un partner puede overridear un subconjunto de los parametros
// del partner (TTLs, gate de email, plans_enabled, webhook_url, vigencia del
// enlace de la plataforma). Este modal lista TODOS los parametros overrideables
// en una tabla: por fila, un slider que decide si el client overridea o usa el
// valor del partner, el input del valor override, y el valor actual del partner
// como referencia.
//
// La logica (campos, armado del objeto a guardar) vive en lib/client-overrides.ts.
// El objeto a guardar CONSERVA las claves guardadas que este modal no muestra.
// Las claves sensibles exigen permiso danger_zone — el backend re-verifica; aca
// solo deshabilitamos su edicion como UX.
//
// A11y: <dialog> nativo (focus-trap del browser) + role="switch" en los sliders.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, X, Lock } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import { patchClient, AdminApiError, type AdminClient, type PartnerDetail } from '@/lib/api';
import {
  OVERRIDE_FIELDS as FIELDS,
  buildOverrides,
  displayValue,
  hiddenOverrideKeys,
  humanSeconds,
  initialRows,
  sameOverrides,
  type OverrideKey,
  type RowState,
  type Rows,
} from '@/lib/client-overrides';
import { fieldCls } from '@/lib/form-styles';

// ---- slider boolean ------------------------------------------------------

function Switch({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-brand-light focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40 ${
        checked ? 'bg-brand' : 'bg-ink-subtle/40 dark:bg-ink-subtle/30'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// ---- modal ---------------------------------------------------------------

export default function ClientOverridesModal({
  open,
  onClose,
  client,
  partner,
  canWrite,
  canEditDanger,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  client: AdminClient;
  partner: PartnerDetail;
  canWrite: boolean;
  canEditDanger: boolean;
  onSaved: () => void;
}): JSX.Element | null {
  const ref = useRef<HTMLDialogElement>(null);
  const [rows, setRows] = useState<Rows>(() => initialRows(client.config_overrides, partner));
  const [confirming, setConfirming] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    if (!open && dlg.open) dlg.close();
  }, [open]);

  // Reset del form cada vez que se (re)abre o cambian los datos del client.
  useEffect(() => {
    if (open) {
      setRows(initialRows(client.config_overrides, partner));
      setConfirming(false);
      setSaveError(null);
    }
  }, [open, client, partner]);

  const { overrides: nextOverrides, errors } = useMemo(
    () => buildOverrides(rows, client.config_overrides),
    [rows, client.config_overrides],
  );
  const hasErrors = Object.keys(errors).length > 0;
  const hiddenKeys = hiddenOverrideKeys(client.config_overrides);

  const dirty = !sameOverrides(client.config_overrides, nextOverrides);

  function setRow(key: OverrideKey, patch: Partial<RowState>): void {
    setRows((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  async function doSave(): Promise<void> {
    setSaving(true);
    setSaveError(null);
    try {
      await patchClient(client.client_id, { config_overrides: nextOverrides });
      setConfirming(false);
      onSaved();
      onClose();
    } catch (e) {
      setSaveError(e instanceof AdminApiError ? e.message : 'Error guardando los overrides.');
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  }

  // Filas activas (para el resumen del confirm): parámetro, valor partner → override.
  const activeSummary = FIELDS.filter((f) => rows[f.key].enabled).map((f) => {
    const rawOverride = (nextOverrides as Record<string, unknown>)[f.key];
    return {
      key: f.key,
      label: f.label,
      partner: displayValue(f, f.partnerValue(partner)),
      override: displayValue(
        f,
        rawOverride === undefined ? null : (rawOverride as boolean | number | string | null),
      ),
    };
  });

  return (
    <>
      <dialog
        ref={ref}
        aria-labelledby="overrides-title"
        onClose={onClose}
        className="rounded-xl p-0 w-full max-w-3xl bg-surface dark:bg-surface-dark text-ink dark:text-ink-inverted backdrop:bg-black/50"
      >
        <div className="flex items-center justify-between border-b border-surface-border dark:border-surface-dark-border px-6 py-4">
          <div>
            <h2 id="overrides-title" className="text-lg font-semibold">
              Overrides del client
            </h2>
            <p className="text-xs font-mono text-ink-muted">{client.client_id}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="rounded p-1 text-ink-muted hover:bg-surface-muted dark:hover:bg-surface-dark-muted"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
          <p className="text-sm text-ink-muted">
            Activá el slider para que este client use un valor propio en vez del valor del
            partner. Slider apagado = usa el valor del partner.
          </p>

          <div className="overflow-x-auto rounded-lg border border-surface-border dark:border-surface-dark-border">
            <table className="w-full min-w-[40rem] text-sm">
              <thead>
                <tr className="border-b border-surface-border dark:border-surface-dark-border text-left text-xs uppercase text-ink-muted">
                  <th className="p-2 font-medium">Parámetro</th>
                  <th className="p-2 font-medium w-24 text-center">Override</th>
                  <th className="p-2 font-medium">Valor del override</th>
                  <th className="p-2 font-medium">Valor del partner</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border dark:divide-surface-dark-border">
                {FIELDS.map((f) => {
                  const st = rows[f.key];
                  const locked = f.danger && !canEditDanger;
                  const disabled = !canWrite || locked;
                  const partnerVal = f.partnerValue(partner);
                  return (
                    <tr
                      key={f.key}
                      className={st.enabled ? 'bg-brand/5' : undefined}
                      {...(f.helpId ? { 'data-help': f.helpId } : {})}
                    >
                      <td className="p-2 align-top">
                        <div className="flex items-center gap-1 font-medium">
                          {f.label}
                          {f.danger ? (
                            <span
                              title="Requiere permiso danger_zone"
                              className="text-status-warning"
                            >
                              <Lock size={11} aria-hidden="true" />
                            </span>
                          ) : null}
                        </div>
                        {f.hint ? (
                          <p className="text-[0.7rem] leading-snug text-ink-muted mt-0.5 max-w-xs">
                            {f.hint}
                          </p>
                        ) : null}
                      </td>
                      <td className="p-2 align-top text-center">
                        <Switch
                          checked={st.enabled}
                          disabled={disabled}
                          onChange={(v) => setRow(f.key, { enabled: v })}
                          label={`Override de ${f.label}`}
                        />
                      </td>
                      <td className="p-2 align-top">
                        {f.kind === 'boolean' ? (
                          <label
                            className={`inline-flex items-center gap-2 text-xs ${
                              st.enabled ? '' : 'opacity-40'
                            }`}
                          >
                            <Switch
                              checked={Boolean(st.value)}
                              disabled={disabled || !st.enabled}
                              onChange={(v) => setRow(f.key, { value: v })}
                              label={`Valor de ${f.label}`}
                            />
                            <span>{st.value ? 'Sí' : 'No'}</span>
                          </label>
                        ) : (
                          <div>
                            <input
                              type={f.kind === 'number' ? 'number' : 'text'}
                              inputMode={f.kind === 'number' ? 'numeric' : undefined}
                              min={f.min}
                              max={f.max}
                              value={String(st.value)}
                              disabled={disabled || !st.enabled}
                              placeholder={
                                f.kind === 'text'
                                  ? 'https://… (vacío = sin webhook)'
                                  : f.emptyMeansUnset
                                    ? 'vacío = sin límite'
                                    : ''
                              }
                              onChange={(e) => setRow(f.key, { value: e.target.value })}
                              className={fieldCls({
                                size: 'sm',
                                mono: f.kind === 'number',
                                invalid: Boolean(errors[f.key]),
                              })}
                            />
                            <div className="flex items-center gap-2 mt-0.5">
                              {f.unit && st.enabled ? (
                                <span className="text-[0.65rem] text-ink-subtle">
                                  en {f.unit === 's' ? 'segundos' : f.unit}
                                  {f.unit === 's' && /^\d+$/.test(String(st.value).trim())
                                    ? ` · ${humanSeconds(Number(String(st.value).trim()))}`
                                    : ''}
                                </span>
                              ) : null}
                              {errors[f.key] ? (
                                <span className="text-[0.65rem] text-status-error">
                                  {errors[f.key]}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="p-2 align-top text-ink-muted">
                        <span className="text-xs">{displayValue(f, partnerVal)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {hiddenKeys.length > 0 ? (
            <p className="text-[0.7rem] text-ink-muted">
              Este client tiene {hiddenKeys.length === 1 ? 'un ajuste' : `${hiddenKeys.length} ajustes`}{' '}
              que esta pantalla no muestra (
              <span className="font-mono">{hiddenKeys.join(', ')}</span>). Se conservan al guardar.
            </p>
          ) : null}

          {!canEditDanger ? (
            <p className="text-[0.7rem] text-ink-muted flex items-center gap-1">
              <Lock size={11} aria-hidden="true" />
              Los parámetros marcados requieren el permiso danger_zone para cambiarse.
            </p>
          ) : null}

          {saveError ? <p className="text-sm text-status-error">{saveError}</p> : null}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-surface-border dark:border-surface-dark-border px-6 py-4">
          <span className="text-xs text-ink-muted">
            {dirty ? 'Hay cambios sin guardar.' : 'Sin cambios.'}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="min-h-[2.75rem] rounded-lg border border-surface-border dark:border-surface-dark-border px-4 py-2 text-sm hover:bg-surface-muted dark:hover:bg-surface-dark-muted"
            >
              Descartar
            </button>
            <button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={!canWrite || !dirty || hasErrors}
              className="min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 disabled:opacity-50"
            >
              Guardar cambios
            </button>
          </div>
        </div>
      </dialog>

      <ConfirmDialog
        open={confirming}
        title="¿Está usted seguro de que quiere hacer estos cambios?"
        confirmLabel="Sí, guardar"
        loading={saving}
        onConfirm={() => void doSave()}
        onCancel={() => setConfirming(false)}
        description={
          <div className="space-y-3">
            {activeSummary.length === 0 ? (
              <p>
                Se quitan <strong>todos</strong> los overrides: este client pasará a usar los
                valores del partner en todos los parámetros.
              </p>
            ) : (
              <>
                <p>
                  El client <span className="font-mono">{client.client_id}</span> quedará con estos
                  overrides (valor del partner → valor del override):
                </p>
                <ul className="space-y-1">
                  {activeSummary.map((s) => (
                    <li
                      key={s.key}
                      className="flex flex-wrap items-center gap-2 rounded border border-surface-border dark:border-surface-dark-border px-2 py-1 text-xs"
                    >
                      <span className="font-medium">{s.label}:</span>
                      <span className="text-ink-muted line-through decoration-ink-subtle/50">
                        {s.partner}
                      </span>
                      <ArrowRight size={13} aria-hidden="true" className="text-brand" />
                      <span className="font-medium text-brand">{s.override}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        }
      />
    </>
  );
}
